import { useState } from "react";
import { motion } from "framer-motion";
import { Quote, ChevronDown, Users } from "lucide-react";
import { Modal } from "@/components/site/Modal";
import { getTestimonials, HOMEPAGE_PRIMARY, HOMEPAGE_MORE } from "@/lib/testimonials";

export const TestimonialCard = ({ t, index = 0, surface = "white" }) => {
  const [open, setOpen] = useState(false);
  const isLong = t.paragraphs.join(" ").length > 360;
  const fadeColor = surface === "ivory" ? "from-ivory" : "from-white";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      data-testid={`testimonial-card-${t.id}`}
      className={`${surface === "ivory" ? "bg-ivory" : "bg-white"} lr-card p-7 md:p-8 flex flex-col`}
    >
      <div className="flex items-center gap-4">
        <img
          src={t.photo}
          alt={t.name}
          loading="lazy"
          className="h-16 w-16 rounded-full object-cover border border-taupe/50"
        />
        <div>
          <p className="font-serif text-xl text-legacy leading-tight" data-testid={`testimonial-name-${t.id}`}>
            {t.name}
          </p>
          <p className="text-bronze text-[0.72rem] tracking-[0.16em] uppercase mt-1">Client Experience</p>
        </div>
      </div>

      <Quote size={26} className="text-gold/50 mt-6" />

      <div
        className={`mt-3 space-y-3 text-slate text-[0.92rem] leading-relaxed ${
          open ? "" : "max-h-[240px] overflow-hidden relative"
        }`}
      >
        {t.paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
        {!open && isLong && (
          <span className={`pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t ${fadeColor} to-transparent`} />
        )}
      </div>

      {isLong && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`testimonial-toggle-${t.id}`}
          className="mt-5 inline-flex items-center gap-1.5 text-bronze font-semibold text-sm hover:text-gold transition-colors self-start"
        >
          {open ? "Show less" : "Read full experience"}
          <ChevronDown size={16} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>
      )}
    </motion.div>
  );
};

export const Testimonials = () => {
  const [more, setMore] = useState(false);
  const primary = getTestimonials(HOMEPAGE_PRIMARY);
  const extra = getTestimonials(HOMEPAGE_MORE);

  return (
    <section id="testimonials" data-testid="testimonials-section" className="bg-[#EFE8D9] py-24 md:py-32">
      <div className="container-lr">
        <div className="max-w-2xl">
          <div className="flex items-center gap-4">
            <span className="gold-rule" />
            <p className="eyebrow">Client Experiences</p>
          </div>
          <h2 className="mt-6 font-serif font-semibold tracking-display text-legacy text-3xl md:text-4xl lg:text-5xl leading-[1.08]">
            Real stories, in their own words.
          </h2>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {primary.map((t, i) => (
            <TestimonialCard key={t.id} t={t} index={i} />
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <button
            type="button"
            onClick={() => setMore(true)}
            data-testid="view-more-testimonials"
            className="btn-base btn-outline-dark"
          >
            <Users size={18} />
            View More Client Experiences
          </button>
        </div>
      </div>

      <Modal open={more} onClose={() => setMore(false)} title="More Client Experiences" maxWidth="max-w-5xl">
        <div className="p-6 sm:p-9 clear-both">
          <p className="eyebrow text-bronze">Client Experiences</p>
          <h3 className="mt-2 font-serif text-2xl md:text-3xl text-legacy leading-tight">
            More stories from the Legacy Life community.
          </h3>
          <div className="mt-8 grid sm:grid-cols-2 gap-6 items-start">
            {extra.map((t) => (
              <TestimonialCard key={t.id} t={t} surface="ivory" />
            ))}
          </div>
        </div>
      </Modal>
    </section>
  );
};
